import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "./utils/api";
import { HOME_SMART_AREA, HOME_SMART_LOGIN, isHomeSmartSite, isHomeSmartPublicPage, migrateLegacyExternalSession, saveSiteSession, clearSiteSession, homeSmartEntryPath, isInternalImpersonation, currentSite, tokenForSite } from './utils/brandSession';
import { SiteBoundary, SiteRedirect, InternalSiteOnly } from './components/home-smart/SiteBoundary';
import "@/App.css";

// Error Boundary for chunk loading errors
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import AppErrorBoundary from "./components/AppErrorBoundary";

// Tour Provider
import { TourProvider } from "./components/tour/TourProvider";

// Components
import LoginPage from "./components/LoginPage";
import ResetPassword from "./components/ResetPassword";
import Dashboard from "./components/Dashboard";
import AdminDashboard from "./components/AdminDashboard";
import KPIAdmin from "./components/KPIAdmin";
import BookingsManager from "./components/BookingsManager";
import InvoicesManager from "./components/InvoicesManager";
import PropertiesManager from "./components/PropertiesManager";
import ClosuresManager from "./components/ClosuresManager";
import ManagementControl from "./components/ManagementControl";
import BudgetForecast from "./components/BudgetForecast";
import Reports from "./components/Reports";
import ReviewsManager from "./components/ReviewsManager";
import PlatformCredentials from "./components/PlatformCredentials";
import PartnersManager from "./components/PartnersManager";
import PropertyManagersManager from "./components/PropertyManagersManager";
import ResponsabiliRubrica from "./pages/ResponsabiliRubrica";
import MypasspartuConsole from "./pages/admin/MypasspartuConsole";
import KPIPartnerManager from "./components/KPIPartnerManager";
import DocumentsUpload from "./components/DocumentsUpload";
import BackupManager from "./components/BackupManager";
import TodayCollections from "./components/TodayCollections";
import MenuCustomizer from "./components/MenuCustomizer";
import BankBalances from "./components/BankBalances";
import ReconciliationPopup from "./components/ReconciliationPopup";
import KPIDashboard from "./components/KPIDashboard";
import NotificationSettings from "./components/NotificationSettings";
import TwoFactorSettings from "./components/TwoFactorSettings";
import DataMissingDashboard from "./components/DataMissingDashboard";
import BookingAnalytics from "./components/BookingAnalytics";
import DataImport from "./components/DataImport";
import ExcelImporter from "./components/ExcelImporter";
import GuestCheckin from "./components/GuestCheckin";
import RedirectPage from "./pages/guest/RedirectPage";
import CheckinManager from "./components/CheckinManager";
import AlloggiatiReceiptsManager from "./components/AlloggiatiReceiptsManager";
import TourismFlowsManager from "./components/TourismFlowsManager";
import FlussiTuristiciManager from "./components/FlussiTuristiciManager";
import GuestDocumentsTable from "./components/GuestDocumentsTable";
import DatabaseSync from "./components/DatabaseSync";
import TestDeploy from "./components/TestDeploy";
import ActivateAccount from "./components/ActivateAccount";
import SpokiTemplatesManager from "./components/SpokiTemplatesManager";
import RegisterResponsible from "./components/RegisterResponsible";
import OwnerPortal from "./pages/OwnerPortal";
import UnlinkedBookings from "./components/UnlinkedBookings";

// Responsabile Pages
import ResponsabileLayout from "./pages/responsabile/ResponsabileLayout";
import Layout from "./components/Layout";
import ResponsabileHome from "./pages/responsabile/ResponsabileHome";
import HomeSmartCommunications from "./pages/responsabile/HomeSmartCommunications";
import HolidayWorldEventContact, { isHolidayWorldEventPage } from './pages/public/HolidayWorldEventContact';
import HolidayWorldContactFeedback from './pages/public/HolidayWorldContactFeedback';
import { CommunicationsRedirect } from './components/home-smart/CommunicationsRedirect';
import HomeSmartCollaborators from "./pages/responsabile/HomeSmartCollaborators";
import HomeSmartOperations from './pages/responsabile/HomeSmartOperations';
import { CollaboratorGate } from './components/home-smart/CollaboratorNavigation';
import { FirstPassword } from './components/home-smart/FirstPassword';
import ResponsibleDashboard from "./components/ResponsibleDashboard";
import ImmobiliGestiti from "./pages/responsabile/ImmobiliGestiti";
import AreaFormazione from "./pages/responsabile/AreaFormazione";
import StoricoPrenotazioni from "./pages/responsabile/StoricoPrenotazioni";
import MioProfilo from "./pages/responsabile/MioProfilo";
import ApriTicket from "./pages/responsabile/ApriTicket";
import Segnalazioni from "./pages/responsabile/Segnalazioni";
import EventiFormazione from "./pages/responsabile/EventiFormazione";
import AdminEventiFormazione from "./pages/admin/EventiFormazione";
import VideoHolly from "./pages/responsabile/VideoHolly";
import ChiusureResponsabile from "./pages/responsabile/ChiusureResponsabile";
import SchedeQualitaList from "./pages/responsabile/SchedeQualitaList";
import SchedaQualita from "./pages/responsabile/SchedaQualita";
import PassaggiConsegne from "./pages/responsabile/PassaggiConsegne";
import PassaggioConsegneDetail from "./pages/responsabile/PassaggioConsegneDetail";
import RicevuteQuestura from "./pages/responsabile/RicevuteQuestura";

// Admin Pages
import AdminFormazione from "./pages/admin/AdminFormazione";
import AdminResponsabili from "./pages/admin/AdminResponsabili";
import AdminSegnalazioni from "./pages/admin/AdminSegnalazioni";
import AdminTickets from "./pages/admin/AdminTickets";
import TicketArchivio from "./pages/admin/TicketArchivio";
import DataAudit from "./pages/admin/DataAudit";
import AlloggiatiAssociation from "./pages/admin/AlloggiatiAssociation";
import SynopticCalendar from "./pages/admin/SynopticCalendar";
import IssuesDashboard from "./pages/admin/IssuesDashboard";
import QuesturaDashboard from "./pages/admin/QuesturaDashboard";
import StatoConfigurazione from "./pages/admin/StatoConfigurazione";
import PropertyConfigPage from "./pages/admin/PropertyConfigPage";
import PropertySchedaPage from "./pages/admin/PropertySchedaPage";
import MidTermListPage from "./pages/admin/MidTermListPage";
import PreventiviPage from "./pages/preventivi/PreventiviPage";
import QuotePublicPage from "./pages/public/QuotePublicPage";
import PartnerQuotesPage from "./pages/public/PartnerQuotesPage";
import HomeSmartPortal from "./pages/public/HomeSmartPortal";
import HomeSmartLanding from "./pages/public/HomeSmartLanding";
import HomeSmartRegister from "./pages/public/HomeSmartRegister";
import HomeSmartEventsPage from "./pages/admin/HomeSmartEventsPage";
import HomeSmartTerms from "./pages/public/HomeSmartTerms";
import HomeSmartPrivacy from "./pages/public/HomeSmartPrivacy";
import HomeSmartDpa from "./pages/public/HomeSmartDpa";
import CompanyProfilesPage from "./pages/CompanyProfilesPage";

const PartnershipCatalogRedirect = () => {
  React.useEffect(() => { window.location.replace('/partnership/'); }, []);
  return null;
};
import PricelistCalendarsPage from "./pages/admin/PricelistCalendarsPage";
import SiteRequestsPage from "./pages/admin/SiteRequestsPage";
import PricingManagerPage from "./pages/admin/PricingManagerPage";
import HwAcademyPricingPage from "./pages/admin/HwAcademyPricingPage";
import QuesturaAnomalyDashboard from "./pages/admin/QuesturaAnomalyDashboard";
import ProlongationConfirmPage from "./pages/ProlongationConfirmPage";
import AdminSchedeQualita from "./pages/admin/AdminSchedeQualita";
import AdminSchedaQualitaCompila from "./pages/admin/AdminSchedaQualitaCompila";
import SchedaQualitaConfronto from "./pages/admin/SchedaQualitaConfronto";
import AdminPassaggiConsegne from "./pages/admin/AdminPassaggiConsegne";
import AdminRegistroPrenotazioni from "./pages/admin/AdminRegistroPrenotazioni";
import IstatDashboard from "./pages/admin/IstatDashboard";
import Ross1000NetworkChecks from "./pages/admin/Ross1000NetworkChecks";
import FormazioneProgresso from "./pages/admin/FormazioneProgresso";
import SocialMediaManager from "./pages/admin/SocialMediaManager";
import GuestVideoCampaigns from "./pages/admin/GuestVideoCampaigns";
import GuestVideoLanding from "./pages/public/GuestVideoLanding";
import TranscriptLanding from "./pages/public/TranscriptLanding";
import LeadsCRM from "./pages/admin/LeadsCRM";
import Ross1000BackfillMonitor from "./pages/admin/Ross1000BackfillMonitor";
import TouristTaxManager from "./pages/admin/TouristTaxManager";
import XmlConverterPage from "./pages/admin/XmlConverterPage";
import CustomerServiceControl from "./pages/admin/CustomerServiceControl";
import CallFeedbackLanding from "./pages/public/CallFeedbackLanding";
import PropertyOwnerLeadForm from "./pages/public/PropertyOwnerLeadForm";
import PrivacyPolicy from "./pages/public/PrivacyPolicy";
import TimbraturaPage from "./pages/TimbraturaPage";
import GuidaDipendente from "./pages/GuidaDipendente";
import RegistraTelefono from "./pages/RegistraTelefono";
import GestionePersonale from "./pages/admin/GestionePersonale";
import ServiceFeePage from "./pages/admin/ServiceFeePage";
import CallerIdPopup from "./components/CallerIdPopup";
import PromoActivationPopup from "./components/PromoActivationPopup";
import PaymentArchivePage from "./pages/admin/PaymentArchivePage";
import OwnerCostsOverviewPage from "./pages/admin/OwnerCostsOverviewPage";
import PaymentContactsPage from "./pages/admin/PaymentContactsPage";
import NewsletterPage from "./pages/admin/NewsletterPage";
import CountryAliasesPage from "./pages/admin/CountryAliasesPage";
import InvoicePaymentArchivePage from "./pages/admin/InvoicePaymentArchivePage";
import RecurringCostsPage from "./pages/admin/RecurringCostsPage";
import SegnalatoriCompensiPage from "./pages/admin/SegnalatoriCompensiPage";
import AutonomiaHWsmartPage from "./pages/admin/AutonomiaHWsmartPage";
import AccountingControlPage from "./pages/admin/AccountingControlPage";
import HomeSmartEconomicsPage from "./pages/admin/HomeSmartEconomicsPage";
// AdminImpersonate rimosso - funzionalità integrata in PropertyManagersManager

// Prenotazioni Pages
import RegistroPrenotazioni from "./pages/prenotazioni/RegistroPrenotazioni";
import GestioneSpesePage from "./pages/GestioneSpesePage";
import PromozioniPage from "./pages/PromozioniPage";
import PrenotazioniDaIncassare from "./pages/prenotazioni/PrenotazioniDaIncassare";
import SovrapposizioniManager from "./pages/prenotazioni/SovrapposizioniManager";

// Public Pages
import PropertyReplyPage from "./pages/PropertyReplyPage";
import PropertyTicketReply from "./pages/PropertyTicketReply";

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const isHomeSmartBrowserContext = () => {
  return isHomeSmartSite();
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null, allowedRoles = null, allowedEmails = null, noLayout = false }) => {
  const { user } = useAuth();
  
  if (!user) {
    return isHomeSmartSite() ? <SiteRedirect to={HOME_SMART_LOGIN} /> : <Navigate to="/login" replace />;
  }
  if (isHomeSmartSite() && !user.is_external_tenant) return <SiteRedirect to={HOME_SMART_LOGIN} />;
  if (user.is_home_smart_collaborator) return <CollaboratorGate user={user}>{children}</CollaboratorGate>;
  
  const roles = allowedRoles || (requiredRole ? (Array.isArray(requiredRole) ? requiredRole : [requiredRole]) : null);
  
  if (roles) {
    const emailAllowed = (allowedEmails || []).includes((user.email || '').toLowerCase());
    if (!roles.includes(user.role) && user.role !== 'admin' && !emailAllowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  // Wrappa automaticamente nel Layout a meno che noLayout=true
  if (noLayout) {
    return children;
  }
  return <Layout>{children}</Layout>;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReconciliationPopup, setShowReconciliationPopup] = useState(false);
  const [companies, setCompanies] = useState([]);

  // Check authentication on app load
  useEffect(() => {
    let active = true;
    const checkAuth = async () => {
      if (isHomeSmartPublicPage() || isHolidayWorldEventPage()) { setLoading(false); return; }
      migrateLegacyExternalSession();
      while (active) {
        if (isHomeSmartPublicPage() || isHolidayWorldEventPage()) { setLoading(false); return; }
        const checkedSite = currentSite();
        const token = tokenForSite(checkedSite);
        if (!token) { setUser(null); setLoading(false); return; }
        try {
          const response = await api.get('/auth/verify', { skipAuthRedirect: true });
          if (!active) return;
          // Re-check the latest session instead of applying an old account's result.
          if (checkedSite !== currentSite() || token !== tokenForSite(checkedSite)) continue;
          const authenticatedUser = response.data.user;
          if (checkedSite === 'home-smart' && !authenticatedUser.is_external_tenant) {
            clearSiteSession(checkedSite, token); setUser(null); setLoading(false); return;
          }
          if (checkedSite !== 'home-smart' && authenticatedUser.is_external_tenant && !isInternalImpersonation()) {
            saveSiteSession(token, authenticatedUser, 'home-smart'); window.location.replace(homeSmartEntryPath()); return;
          }
          setUser(authenticatedUser);
          localStorage.setItem('hw_user', JSON.stringify(authenticatedUser));
          if (authenticatedUser?.is_external_tenant) localStorage.setItem('home_smart_context', 'true');
          else localStorage.removeItem('home_smart_context');
          if (authenticatedUser?.role === 'admin') {
            checkReconciliationStatus(authenticatedUser);
          } else {
            setShowReconciliationPopup(false);
            setCompanies([]);
          }
        } catch (error) {
          if (!active) return;
          if (checkedSite !== currentSite() || token !== tokenForSite(checkedSite)) continue;
          console.warn('[Auth] Token verification failed:', error.message);
          if (error?.response?.status === 401) clearSiteSession(checkedSite, token);
        }
        setLoading(false);
        return;
      }
    };

    checkAuth();
    return () => { active = false; };
  }, []);

  const checkReconciliationStatus = async (currentUser) => {
    if (currentUser?.role !== 'admin') {
      setShowReconciliationPopup(false);
      setCompanies([]);
      return;
    }
    // Check if popup was already shown today
    const storageKey = `reconciliation_popup_shown_${currentUser.id || currentUser.email}`;
    const lastShown = localStorage.getItem(storageKey);
    const today = new Date().toDateString();
    
    if (lastShown === today) {
      return; // Already shown today
    }
    
    try {
      // Fetch companies
      const companiesResponse = await api.get('/companies', { timeout: 10000 });
      setCompanies(companiesResponse.data);
      
      // Check if there are discrepancies (with short timeout to avoid blocking UI)
      let hasDiscrepancies = false;
      for (const company of companiesResponse.data) {
        try {
          const statusResponse = await api.get(`/reconciliation-status/${company.id}`, { timeout: 15000 });
          if (statusResponse.data.has_discrepancies) {
            hasDiscrepancies = true;
            break;
          }
        } catch (companyError) {
          // Skip this company if timeout, don't block the UI
          console.warn(`Reconciliation check skipped for ${company.name}: timeout`);
          continue;
        }
      }
      
      if (hasDiscrepancies) {
        setShowReconciliationPopup(true);
        localStorage.setItem(storageKey, today);
      }
    } catch (error) {
      console.error('Error checking reconciliation status:', error);
    }
  };

  const establishSession = (accessToken, authenticatedUser) => {
    if (isHomeSmartSite() && !authenticatedUser?.is_external_tenant) throw new Error('Accesso non disponibile. Verifica le credenziali.');
    saveSiteSession(accessToken, authenticatedUser, isInternalImpersonation() && !isHomeSmartSite() ? 'hw-smart' : undefined);
    if (authenticatedUser?.is_external_tenant) localStorage.setItem('home_smart_context', 'true');
    else localStorage.removeItem('home_smart_context');
    setUser(authenticatedUser);
    if (authenticatedUser?.role === 'admin') {
      checkReconciliationStatus(authenticatedUser);
    } else {
      setShowReconciliationPopup(false);
      setCompanies([]);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password }, {params: {brand: isHomeSmartSite() ? 'home-smart' : undefined}, skipAuthRedirect: true});
      const { access_token, user } = response.data;
      
      establishSession(access_token, user);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    const homeSmart = isHomeSmartSite();
    clearSiteSession(currentSite());
    localStorage.removeItem('home_smart_context');
    setShowReconciliationPopup(false);
    setCompanies([]);
    setUser(null);
    if (homeSmart) window.location.assign(HOME_SMART_LOGIN);
  };

  if (isHolidayWorldEventPage()) {
    return <AppErrorBoundary><BrowserRouter><Routes><Route path="/holiday-world/evento/:code/:audience" element={<HolidayWorldEventContact />} /><Route path="/holiday-world/esito/:token/:outcome" element={<HolidayWorldContactFeedback />} /><Route path="*" element={<HolidayWorldEventContact />} /></Routes></BrowserRouter></AppErrorBoundary>;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-slate-600">{isHomeSmartBrowserContext() ? 'Caricamento Home Smart...' : 'Caricamento HWsmart...'}</p>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary>
    <ChunkErrorBoundary>
      <AuthContext.Provider value={{ user, login, logout, establishSession }}>
        <TourProvider>
          <div className="App">
            <div data-testid="app-notifications">
            <Toaster
              className={isHomeSmartSite() ? 'home-smart-toaster' : undefined}
              position="top-center"
              richColors
              closeButton
              expand
              duration={5000}
              toastOptions={{ style: { fontSize: '14px' } }}
            />
            </div>
            <BrowserRouter basename={window.location.pathname.startsWith(HOME_SMART_AREA) ? HOME_SMART_AREA : undefined}>
              <SiteBoundary>
              <InternalSiteOnly><CallerIdPopup /><PromoActivationPopup /></InternalSiteOnly>
              <Routes>
              {/* Public Routes */}
            <Route 
              path="/login" 
              element={isHomeSmartSite() ? <SiteRedirect to={HOME_SMART_LOGIN} /> : user ? <Navigate to={user.role === 'employee' ? '/timbratura' : '/dashboard'} replace /> : <LoginPage />} 
            />
            <Route
              path="/home-smart/accedi"
              element={<LoginPage brand="home-smart" />}
            />
            <Route path="/home-smart/reimposta-password" element={<ResetPassword brand="home-smart" />} />
            <Route path="/home-smart/area/*" element={<SiteRedirect to={window.location.pathname + window.location.search} />} />
            <Route 
              path="/reset-password" 
              element={<ResetPassword />} 
            />
            <Route 
              path="/guest-checkin/:token" 
              element={<GuestCheckin />} 
            />
            <Route 
              path="/v/:token"
              element={<GuestVideoLanding />}
            />
            <Route
              path="/f/:token"
              element={<CallFeedbackLanding />}
            />
            <Route
              path="/t/:token"
              element={<TranscriptLanding />}
            />
            <Route
              path="/privacy"
              element={<PrivacyPolicy />}
            />
            <Route
              path="/richiesta-proprietari"
              element={<PropertyOwnerLeadForm />}
            />
            <Route 
              path="/guest-checkin" 
              element={<GuestCheckin />} 
            />
            <Route 
              path="/r" 
              element={<RedirectPage />} 
            />
            <Route 
              path="/activate-account/:token" 
              element={<ActivateAccount />} 
            />
            <Route 
              path="/register" 
              element={<RegisterResponsible />} 
            />
            <Route
              path="/owner-portal/:token"
              element={<OwnerPortal />}
            />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company-profiles"
              element={
                <ProtectedRoute requiredRole="admin">
                  <CompanyProfilesPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <KPIAdmin />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/eventi-formazione"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminEventiFormazione />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/backup"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BackupManager />
                </ProtectedRoute>
              }
            />
            
            {/* Operator Routes */}
            <Route
              path="/bookings"
              element={
                <ProtectedRoute requiredRole={["bookings_operator", "property_responsible"]}>
                  <BookingsManager />
                </ProtectedRoute>
              }
            />
            
            {/* today-collections redirects to unified page */}
            <Route path="/today-collections" element={<Navigate to="/prenotazioni-da-incassare" replace />} />

            <Route
              path="/prenotazioni-da-incassare"
              element={
                <ProtectedRoute requiredRole={["bookings_operator"]}>
                  <PrenotazioniDaIncassare />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sovrapposizioni"
              element={
                <ProtectedRoute requiredRole={["bookings_operator"]}>
                  <SovrapposizioniManager />
                </ProtectedRoute>
              }
            />

            
            <Route
              path="/invoices"
              element={
                <ProtectedRoute requiredRole="invoice_operator">
                  <InvoicesManager />
                </ProtectedRoute>
              }
            />

            
            <Route
              path="/kpi-dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <KPIDashboard />
                </ProtectedRoute>
              }
            />

            
            <Route
              path="/site-requests"
              element={
                <ProtectedRoute allowedRoles={["customer_service", "bookings_operator"]}>
                  <SiteRequestsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/properties"
              element={
                <ProtectedRoute requiredRole="customer_service">
                  <PropertiesManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/closures"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ClosuresManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/management-control"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ManagementControl />
                </ProtectedRoute>
              }
            />

            
            <Route
              path="/bank-balances"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BankBalances />
                </ProtectedRoute>
              }
            />

            
            <Route
              path="/budget-forecast"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BudgetForecast />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Reports />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reviews"
              element={
                <ProtectedRoute>
                  <ReviewsManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/credentials"
              element={
                <ProtectedRoute requiredRole={["admin", "customer_service"]}>
                  <PlatformCredentials />
                </ProtectedRoute>
              }
            />
            <Route
              path="/xml-converter"
              element={
                <ProtectedRoute requiredRole={["admin", "customer_service"]}>
                  <XmlConverterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cs-control"
              element={
                <ProtectedRoute requiredRole={["admin"]} allowedEmails={["partnerinvoice@holidayw.eu"]}>
                  <CustomerServiceControl />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gestione-personale"
              element={
                <ProtectedRoute requiredRole={["admin"]}>
                  <GestionePersonale />
                </ProtectedRoute>
              }
            />
            <Route
              path="/canone-annuale"
              element={
                <ProtectedRoute requiredRole={["admin"]}>
                  <ServiceFeePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/timbratura"
              element={
                <ProtectedRoute noLayout={true}>
                  <TimbraturaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registra-telefono"
              element={<RegistraTelefono />}
            />
            <Route
              path="/guida-timbratura"
              element={
                <ProtectedRoute noLayout={true}>
                  <GuidaDipendente />
                </ProtectedRoute>
              }
            />

            <Route
              path="/payment-archive"
              element={
                <ProtectedRoute requiredRole={["admin", "customer_service", "bookings_operator", "invoice_operator", "marketing"]}>
                  <PaymentArchivePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/payment-contacts"
              element={
                <ProtectedRoute requiredRole={["admin", "bookings_operator", "invoice_operator", "customer_service"]}>
                  <PaymentContactsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/newsletter"
              element={
                <ProtectedRoute requiredRole={["admin", "marketing"]}>
                  <NewsletterPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/country-aliases"
              element={
                <ProtectedRoute requiredRole={["admin", "bookings_operator", "invoice_operator", "customer_service"]}>
                  <CountryAliasesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/invoice-payment-archive"
              element={
                <ProtectedRoute requiredRole={["admin", "invoice_operator"]}>
                  <InvoicePaymentArchivePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/owner-costs-overview"
              element={
                <ProtectedRoute requiredRole={["admin"]}>
                  <OwnerCostsOverviewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/recurring-costs"
              element={
                <ProtectedRoute requiredRole={["admin", "invoice_operator"]}>
                  <RecurringCostsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/accounting-control"
              element={
                <ProtectedRoute requiredRole={["admin", "invoice_operator", "customer_service", "bookings_operator"]}>
                  <AccountingControlPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/compensi-segnalatori"
              element={
                <ProtectedRoute requiredRole={["admin", "invoice_operator"]}>
                  <SegnalatoriCompensiPage />
                </ProtectedRoute>
              }
            />



            {/* Marketing Routes */}
            <Route
              path="/partners"
              element={
                <ProtectedRoute requiredRole={["admin", "marketing"]}>
                  <PartnersManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/property-managers"
              element={
                <ProtectedRoute requiredRole={["admin", "marketing"]}>
                  <PropertyManagersManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/responsabili-rubrica"
              element={
                <ProtectedRoute requiredRole={["admin", "bookings_operator"]}>
                  <ResponsabiliRubrica />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mypasspartu"
              element={
                <ProtectedRoute requiredRole="admin">
                  <MypasspartuConsole />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kpi-partner-manager"
              element={
                <ProtectedRoute requiredRole={["admin", "marketing"]}>
                  <KPIPartnerManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/menu-customizer"
              element={
                <ProtectedRoute requiredRole="admin">
                  <MenuCustomizer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notification-settings"
              element={
                <ProtectedRoute requiredRole="admin">
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/booking-analytics"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BookingAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/2fa-settings"
              element={
                <ProtectedRoute>
                  <TwoFactorSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/missing-data"
              element={
                <ProtectedRoute>
                  <DataMissingDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-import"
              element={
                <ProtectedRoute requiredRole="admin">
                  <DataImport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/excel-import"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ExcelImporter />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Formazione - Gestione Video e Quiz */}
            <Route
              path="/admin/formazione"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service"]}>
                    <AdminFormazione canManageAttachments={user?.role === 'admin'} />
                </ProtectedRoute>
              }
            />

            {/* Progresso Formazione Partner */}
            <Route
              path="/admin/formazione/progresso"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service"]}>
                    <FormazioneProgresso />
                </ProtectedRoute>
              }
            />
            
            {/* Admin/Ufficio Arrivi e Partenze - stesse funzionalità dei Responsabili
                (Verifica/modifica documenti + invio Questura senza blocco 24h) */}
            <Route
              path="/admin/arrivi-partenze"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service", "bookings_operator"]}>
                  <ResponsibleDashboard />
                </ProtectedRoute>
              }
            />

            {/* Tassa di Soggiorno (IDS) */}
            <Route
              path="/admin/tassa-soggiorno"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service", "invoice_operator", "bookings_operator"]}>
                  <TouristTaxManager />
                </ProtectedRoute>
              }
            />
            
            <Route path="/admin/istat/connessioni" element={<ProtectedRoute requiredRole="admin"><Ross1000NetworkChecks /></ProtectedRoute>} />
            {/* ISTAT Dashboard */}
            <Route
              path="/admin/istat"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service"]}>
                  <IstatDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/checkin-manager"
              element={
                <ProtectedRoute>
                  <CheckinManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkin-manager/configurazione"
              element={
                <ProtectedRoute>
                  <StatoConfigurazione />
                </ProtectedRoute>
              }
            />
            <Route
              path="/immobili/:propertyId"
              element={
                <ProtectedRoute requiredRole="admin">
                  <PropertyConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties/:propertyId/scheda"
              element={
                <ProtectedRoute requiredRole="customer_service">
                  <PropertySchedaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calendari-listini"
              element={
                <ProtectedRoute requiredRole={["admin", "bookings_operator"]}>
                  <PricelistCalendarsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/autonomia-hwsmart"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AutonomiaHWsmartPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/medio-termine"
              element={
                <ProtectedRoute requiredRole={["admin"]} allowedEmails={["prenotazioni@holidayw.eu"]}>
                  <MidTermListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/preventivi"
              element={
                <ProtectedRoute requiredRole={["admin", "customer_service", "bookings_operator"]}>
                  <PreventiviPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/listini"
              element={
                <ProtectedRoute requiredRole="customer_service">
                  <PricingManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/proposte-prezzi"
              element={
                <ProtectedRoute requiredRole="admin">
                  <HwAcademyPricingPage />
                </ProtectedRoute>
              }
            />
            
            {/* AlloggiatiWeb Receipts - Admin Only */}
            <Route
              path="/alloggiati-receipts"
              element={
                <ProtectedRoute requiredRole="admin">
                    <AlloggiatiReceiptsManager />
                </ProtectedRoute>
              }
            />
            
            {/* AlloggiatiWeb Bulk Association - Admin Only */}
            <Route
              path="/admin/alloggiati-association"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AlloggiatiAssociation />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Impersonate - RIMOSSO: funzionalità integrata in PropertyManagersManager */}
            
            <Route
              path="/tourism-flows"
              element={
                <ProtectedRoute requiredRole="admin">
                  <TourismFlowsManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/flussi-turistici"
              element={
                <ProtectedRoute requiredRole="admin">
                  <FlussiTuristiciManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/guest-documents"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator", "customer_service"]}>
                  <GuestDocumentsTable />
                </ProtectedRoute>
              }
            />
            
            {/* Database Sync - Solo Admin */}
            <Route
              path="/database-sync"
              element={
                <ProtectedRoute requiredRole="admin">
                  <DatabaseSync />
                </ProtectedRoute>
              }
            />
            
            {/* Test Deploy Page */}
            <Route
              path="/test-deploy"
              element={
                <ProtectedRoute requiredRole="admin">
                  <TestDeploy />
                </ProtectedRoute>
              }
            />

            {/* Spoki WhatsApp Templates */}
            <Route
              path="/admin/spoki-templates"
              element={
                <ProtectedRoute requiredRole="admin">
                    <SpokiTemplatesManager />
                </ProtectedRoute>
              }
            />

            {/* Unlinked Bookings Page */}
            <Route
              path="/admin/unlinked-bookings"
              element={
                <ProtectedRoute requiredRole="admin">
                    <UnlinkedBookings />
                </ProtectedRoute>
              }
            />

            {/* Duplicates Manager Page */}
            
            {/* Property Responsible Routes */}
            <Route
              path="/documents"
              element={
                <ProtectedRoute requiredRole="property_responsible">
                  <DocumentsUpload />
                </ProtectedRoute>
              }
            />
            
            {/* Responsabile Portal Routes */}
            <Route
              path="/responsabile"
              element={
                <ProtectedRoute requiredRole="property_responsible" noLayout>
                  <ResponsabileLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={user?.is_home_smart_collaborator ? <HomeSmartOperations key={`dashboard-${user.id}`} /> : <ResponsabileHome />} />
              <Route path="arrivi-partenze" element={user?.is_home_smart_collaborator ? <HomeSmartOperations key={`arrivals-${user.id}`} view="arrivals" /> : <ResponsibleDashboard />} />
              <Route path="immobili" element={user?.is_home_smart_collaborator ? <HomeSmartOperations key={`properties-${user.id}`} view="properties" /> : <ImmobiliGestiti />} />
              <Route path="formazione" element={<AreaFormazione />} />
              <Route path="eventi-formazione" element={user?.is_external_tenant ? <Navigate to="/responsabile/dashboard" replace /> : <EventiFormazione />} />
              <Route path="storico" element={user?.is_home_smart_collaborator ? <HomeSmartOperations key={`history-${user.id}`} view="history" /> : <StoricoPrenotazioni />} />
              <Route path="profilo" element={user?.is_home_smart_collaborator ? <FirstPassword forced={false} /> : <MioProfilo />} />
              <Route path="marketing" element={user?.is_external_tenant ? <HomeSmartCommunications /> : <Navigate to="/responsabile/dashboard" replace />} />
              <Route path="comunicazioni" element={<CommunicationsRedirect />} />
              <Route path="collaboratori" element={user?.is_external_tenant ? <HomeSmartCollaborators /> : <Navigate to="/responsabile/dashboard" replace />} />
              {user?.is_home_smart_collaborator && <Route path="*" element={<Navigate to="/responsabile/dashboard" replace />} />}
              <Route path="ticket" element={<ApriTicket />} />
              <Route path="segnalazioni" element={<Segnalazioni />} />
              <Route path="video-holly" element={<VideoHolly />} />
              <Route path="calendario-sinottico" element={user?.is_home_smart_collaborator ? <HomeSmartOperations key={`calendar-${user.id}`} view="calendar" /> : <SynopticCalendar />} />
              <Route path="chiusure" element={<ChiusureResponsabile />} />
              <Route path="schede-qualita" element={<SchedeQualitaList />} />
              <Route path="scheda-qualita/:propertyId" element={<SchedaQualita />} />
              <Route path="passaggi-consegne" element={<PassaggiConsegne />} />
              <Route path="passaggio-consegne/:handoverId" element={<PassaggioConsegneDetail />} />
              <Route path="ricevute-questura" element={<RicevuteQuestura />} />
            </Route>

            <Route
              path="/admin/home-smart-economics"
              element={
                <ProtectedRoute requiredRole="admin">
                  <HomeSmartEconomicsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Property Reply Page (Public - for property owners) */}
            <Route path="/property-reply/:token" element={<PropertyReplyPage />} />
            
            {/* Property Ticket Reply Page (Public - for property owners) */}
            <Route path="/property-ticket/:token" element={<PropertyTicketReply />} />
            
            {/* Admin Segnalazioni */}
            <Route
              path="/admin/segnalazioni"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator"]}>
                  <AdminSegnalazioni />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Tickets - Gestione Ticket completa */}
            <Route
              path="/admin/tickets"
              element={
                <ProtectedRoute requiredRole={["admin", "bookings_operator", "customer_service", "invoice_operator"]}>
                  <AdminTickets />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Ticket Archivio - Ticket chiusi */}
            <Route
              path="/admin/tickets/archivio"
              element={
                <ProtectedRoute requiredRole={["admin", "bookings_operator", "customer_service", "invoice_operator"]}>
                  <TicketArchivio />
                </ProtectedRoute>
              }
            />
            
            {/* Dashboard Criticità Immobili */}
            <Route
              path="/admin/data-audit"
              element={
                <ProtectedRoute requiredRole={["admin"]}>
                  <DataAudit />
                </ProtectedRoute>
              }
            />

            {/* Dashboard Criticità Immobili */}
            <Route
              path="/admin/criticita"
              element={
                <ProtectedRoute requiredRole={["admin", "bookings_operator", "customer_service"]}>
                  <IssuesDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Dashboard Questura - Errori AlloggiatiWeb */}
            <Route
              path="/admin/questura-dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <QuesturaDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/questura-anomalies"
              element={
                <ProtectedRoute requiredRole="admin">
                  <QuesturaAnomalyDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Responsabili Management */}
            <Route
              path="/admin/responsabili"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminResponsabili />
                </ProtectedRoute>
              }
            />
            
            {/* Synoptic Calendar - Admin & Property Responsible */}
            <Route
              path="/admin/calendario-sinottico"
              element={
                <ProtectedRoute requiredRole={["admin", "property_responsible"]}>
                  <SynopticCalendar />
                </ProtectedRoute>
              }
            />

            {/* Social Media AI Manager */}
            <Route
              path="/social-media"
              element={
                <ProtectedRoute requiredRole={["admin", "marketing"]}>
                  <SocialMediaManager />
                </ProtectedRoute>
              }
            />

            {/* Guest Video Engagement (WhatsApp via Spoki, A/B/C test, lifelong KPI) */}
            <Route
              path="/guest-videos"
              element={
                <ProtectedRoute requiredRole={["admin", "marketing"]}>
                  <GuestVideoCampaigns />
                </ProtectedRoute>
              }
            />

            {/* CRM Lead Proprietari (V3) e Partner (V4) */}
            <Route
              path="/leads-crm"
              element={
                <ProtectedRoute requiredRole={["admin", "marketing"]}>
                  <LeadsCRM />
                </ProtectedRoute>
              }
            />

            {/* ROSS1000 Backfill Monitor */}
            <Route
              path="/admin/ross1000-backfill"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service"]}>
                  <Ross1000BackfillMonitor />
                </ProtectedRoute>
              }
            />

            
            {/* Quality Sheets - Admin */}
            <Route
              path="/admin/schede-qualita"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator"]}>
                  <AdminSchedeQualita />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/scheda-qualita/:propertyId/compila"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator"]}>
                  <AdminSchedaQualitaCompila />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/scheda-qualita/:propertyId/confronto"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator"]}>
                  <SchedaQualitaConfronto />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Passaggi Consegne */}
            <Route
              path="/admin/passaggi-consegne"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator"]}>
                  <AdminPassaggiConsegne />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/passaggio-consegne/:handoverId"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator"]}>
                  <PassaggioConsegneDetail />
                </ProtectedRoute>
              }
            />
            
            {/* Registro Prenotazioni */}
            <Route
              path="/gestione-spese"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service"]}>
                  <GestioneSpesePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/promozioni"
              element={
                <ProtectedRoute allowedRoles={["admin", "customer_service", "bookings_operator"]}>
                  <PromozioniPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prenotazioni/registro"
              element={
                <ProtectedRoute allowedRoles={["admin", "bookings_operator", "invoice_operator"]}>
                  <RegistroPrenotazioni />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/registro-prenotazioni"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminRegistroPrenotazioni />
                </ProtectedRoute>
              }
            />
            
            {/* Public Routes (no auth required) */}
            <Route path="/preventivo/:token" element={<QuotePublicPage />} />
            <Route path="/partner/preventivi/:token" element={<PartnerQuotesPage />} />
            <Route path="/partner" element={<PartnershipCatalogRedirect />} />
            <Route path="/prolongation-confirm/:token" element={<ProlongationConfirmPage />} />
            <Route path="/home-smart/portal" element={<HomeSmartPortal />} />
            <Route path="/admin/home-smart-events" element={<ProtectedRoute><HomeSmartEventsPage /></ProtectedRoute>} />
            <Route path="/home-smart/evento/:code" element={<HomeSmartLanding />} />
            <Route path="/home-smart/registrazione" element={<HomeSmartRegister />} />
            <Route path="/home-smart/condizioni" element={<HomeSmartTerms />} />
            <Route path="/home-smart/privacy" element={<HomeSmartPrivacy />} />
            <Route path="/home-smart/dpa" element={<HomeSmartDpa />} />
            <Route path="/home-smart" element={<HomeSmartLanding />} />
            <Route path="/home-smart/*" element={<Navigate to="/home-smart" replace />} />

            {/* Default Route */}
            <Route path="/" element={<Navigate to={user?.role === 'employee' ? '/timbratura' : '/dashboard'} replace />} />
          </Routes>
          <InternalSiteOnly>
          {user?.role === 'admin' && <ReconciliationPopup
            show={showReconciliationPopup && !window.location.pathname.startsWith('/admin/accounting-control')}
            onClose={() => setShowReconciliationPopup(false)} companies={companies} />}
          </InternalSiteOnly>
          </SiteBoundary>
        </BrowserRouter>
        
        {/* Reconciliation Popup */}
      </div>
    </TourProvider>
    </AuthContext.Provider>
    </ChunkErrorBoundary>
    </AppErrorBoundary>
  );
}

export default App;